import { createSlice } from "@reduxjs/toolkit";
import { logOut } from "../auth/authSlice";
import {
  getOrderMessages,
  sendOrderMessage,
  markOrderChatRead,
} from "./orderChatActions";

const initialState = {
  openOrderId: null, // numeric id of the order whose drawer is open
  openOrderNumber: null, // its human order number, for the drawer title
  threads: {}, // { [orderId]: { items, loading, loaded, error } }
  unread: {}, // { [orderId]: count } — seeded from history rows
  sending: false,
  sendError: null,
};

const emptyThread = () => ({
  items: [],
  loading: false,
  loaded: false,
  error: null,
});

const ensureThread = (state, orderId) => {
  if (!state.threads[orderId]) {
    state.threads[orderId] = emptyThread();
  }
  return state.threads[orderId];
};

// Messages are appended in arrival order and deduped on id — the same row can
// come back from POST and from the socket, or twice after a reconnect.
const appendUnique = (thread, message) => {
  if (!message?.id) {
    return false;
  }
  if (thread.items.some((m) => m?.id === message?.id)) {
    return false;
  }
  thread.items.push(message);
  return true;
};

const orderChatSlice = createSlice({
  name: "orderChat",
  initialState,
  reducers: {
    openChat: (state, action) => {
      state.openOrderId = action?.payload?.orderId ?? null;
      state.openOrderNumber = action?.payload?.orderNumber ?? null;
      state.sendError = null;
    },
    closeChat: (state) => {
      state.openOrderId = null;
      state.openOrderNumber = null;
      state.sendError = null;
    },
    // `order_message` frame: a message landed. Appended when that thread is
    // already loaded (otherwise the next open fetches it anyway). A staff
    // reply on a thread the customer is not looking at bumps its badge.
    socketMessage: (state, action) => {
      const message = action?.payload;
      const orderId = message?.order_id;
      if (!orderId || !message?.id) {
        return;
      }
      const thread = state.threads[orderId];
      let isNew = true;
      if (thread?.loaded) {
        isNew = appendUnique(thread, message);
      }
      if (
        isNew &&
        message?.direction === "OUTBOUND" &&
        state.openOrderId !== orderId
      ) {
        state.unread[orderId] = (state.unread[orderId] || 0) + 1;
      }
    },
    // `order_message_read` frame: this customer read the thread in another
    // tab. Take the server's count as truth.
    socketRead: (state, action) => {
      const orderId = action?.payload?.order_id;
      if (!orderId) {
        return;
      }
      state.unread[orderId] = action?.payload?.unread_count ?? 0;
    },
    // Every history fetch overwrites the local counts with server truth for
    // the rows it returned; rows on other pages keep whatever they had.
    seedUnreadFromHistory: (state, action) => {
      (action?.payload || []).forEach((row) => {
        if (row?.id !== undefined && row?.id !== null) {
          state.unread[row.id] = row?.unread_message_count ?? 0;
        }
      });
    },
    resetOrderChatError: (state) => {
      state.sendError = null;
      if (state.openOrderId && state.threads[state.openOrderId]) {
        state.threads[state.openOrderId].error = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrderMessages.pending, (state, action) => {
        const thread = ensureThread(state, action?.meta?.arg?.orderId);
        thread.loading = true;
        thread.error = null;
      })
      .addCase(getOrderMessages.fulfilled, (state, action) => {
        const thread = ensureThread(state, action?.meta?.arg?.orderId);
        const fetched = Array.isArray(action?.payload) ? action.payload : [];
        // Anything the socket delivered while the GET was in flight is
        // already newer than the fetched list, so it stays.
        const pendingIds = new Set(fetched.map((m) => m?.id));
        const extras = thread.items.filter((m) => !pendingIds.has(m?.id));
        thread.items = [...fetched, ...extras];
        thread.loading = false;
        thread.loaded = true;
        thread.error = null;
      })
      .addCase(getOrderMessages.rejected, (state, action) => {
        const thread = ensureThread(state, action?.meta?.arg?.orderId);
        thread.loading = false;
        thread.error = action?.payload;
      })
      .addCase(sendOrderMessage.pending, (state) => {
        state.sending = true;
        state.sendError = null;
      })
      .addCase(sendOrderMessage.fulfilled, (state, action) => {
        state.sending = false;
        const orderId = action?.payload?.order_id ?? action?.meta?.arg?.orderId;
        const thread = ensureThread(state, orderId);
        appendUnique(thread, action?.payload);
      })
      .addCase(sendOrderMessage.rejected, (state, action) => {
        state.sending = false;
        state.sendError = action?.payload;
      })
      .addCase(markOrderChatRead.fulfilled, (state, action) => {
        const orderId = action?.meta?.arg?.orderId;
        if (orderId !== undefined && orderId !== null) {
          state.unread[orderId] = action?.payload?.unread_count ?? 0;
        }
      })
      // Logout wipes everything, like every customer-scoped slice, so another
      // customer's thread never flashes on this browser.
      .addCase(logOut, () => initialState);
  },
});

export const {
  openChat,
  closeChat,
  socketMessage,
  socketRead,
  seedUnreadFromHistory,
  resetOrderChatError,
} = orderChatSlice.actions;
export default orderChatSlice.reducer;
